import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Check, Clock3, LockKeyhole } from 'lucide-react';
import { annualOfferContinuePath, annualOfferRequest } from '../../utils/annualOffer';
import { capturePostHogEvent } from '../../utils/analytics';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import './AnnualOfferPage.css';

type Offer = {
  id: string;
  productName: string;
  productSlug: string;
  variant: string;
  monthsFree: number;
  expiresAt: string;
  status: 'open' | 'pending' | 'completed';
  quote: {
    currency: string;
    annualAmount: number;
    monthlyCredit: number;
    taxAmount: number;
    accountCredit: number;
    amountDue: number;
    renewalAmount: number;
    renewalDate: string;
    cardBrand: string;
    cardLast4: string;
  };
};

export default function AnnualOfferPage() {
  const { getApiToken, isAuthenticated, isLoading } = useUnifiedAuth();
  const billingRequest = useCallback(
    async (action: '' | 'accept' | 'decline' = '', id?: string) =>
      annualOfferRequest(action, id, isAuthenticated ? await getApiToken() : null),
    [getApiToken, isAuthenticated],
  );
  return <AnnualOfferCheckout billingRequest={billingRequest} isLoading={isLoading} />;
}

export function AnnualOfferCheckout({
  billingRequest,
  isLoading = false,
}: {
  billingRequest: (action?: '' | 'accept' | 'decline', id?: string) => Promise<any>;
  isLoading?: boolean;
}) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const continuation = annualOfferContinuePath(search);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const anchor = useRef({ serverNow: 0, localNow: 0 });
  const request = useRef<Promise<any> | null>(null);
  const actionInFlight = useRef(false);

  const track = (event: string, current: Offer) =>
    capturePostHogEvent(event, {
      annual_offer_id: current.id,
      product_slug: current.productSlug,
      annual_offer_variant: current.variant,
      months_free: current.monthsFree,
      currency: current.quote.currency,
      amount_due_cents: current.quote.amountDue,
    });

  useEffect(() => {
    let active = true;
    if (isLoading) return;
    if (!request.current) request.current = billingRequest();
    request.current
      .then((result) => {
        if (!active) return;
        setLoaded(true);
        if (!result.offer) {
          navigate(continuation, { replace: true });
          return;
        }
        anchor.current = { serverNow: result.serverNow, localNow: performance.now() };
        setRemaining(
          Math.max(0, Math.ceil((Date.parse(result.offer.expiresAt) - result.serverNow) / 1000)),
        );
        setOffer(result.offer);
        setConfirmed(result.offer.status === 'completed');
        track('annual_offer_viewed', result.offer);
      })
      .catch((requestError) => {
        if (!active) return;
        setLoaded(true);
        setError(requestError.message);
      });
    return () => {
      active = false;
    };
  }, [continuation, navigate, isLoading, billingRequest]);

  useEffect(() => {
    if (!offer) return;
    const tick = () => {
      const now = anchor.current.serverNow + performance.now() - anchor.current.localNow;
      setRemaining(Math.max(0, Math.ceil((Date.parse(offer.expiresAt) - now) / 1000)));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [offer]);

  const money = (cents: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: offer?.quote.currency || 'USD',
    }).format(cents / 100);
  const claim = async () => {
    if (!offer || actionInFlight.current) return;
    actionInFlight.current = true;
    setBusy(true);
    setError('');
    try {
      track('annual_offer_accept_clicked', offer);
      let result = await billingRequest('accept', offer.id);
      if (result.status === 'pending') setOffer({ ...offer, status: 'pending' });
      if (result.status === 'pending' && result.paymentStatus === 'requires_payment_method')
        throw new Error(
          result.paymentError || 'Your card was declined. Your monthly plan remains active.',
        );
      if (result.status === 'pending' && result.clientSecret) {
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PK || '');
        if (!stripe) throw new Error('The secure payment form could not load. Please retry.');
        const confirmation = await stripe.confirmCardPayment(result.clientSecret);
        if (confirmation.error)
          throw new Error(
            confirmation.error.message ||
              'Payment needs attention. Your monthly plan remains active.',
          );
        result = await billingRequest('accept', offer.id);
      }
      if (result.status !== 'completed') {
        setOffer({ ...offer, status: 'pending' });
        throw new Error(
          'Payment is still awaiting confirmation. Retry to check the same invoice. Your monthly plan remains active until payment succeeds.',
        );
      }
      setConfirmed(true);
      track('annual_offer_switch_verified', offer);
    } catch (claimError) {
      setError(
        claimError instanceof Error
          ? claimError.message
          : 'The payment result could not be verified. Please retry.',
      );
    } finally {
      setBusy(false);
      actionInFlight.current = false;
    }
  };
  const keepMonthly = async () => {
    if (actionInFlight.current) return;
    if (!offer) {
      navigate(continuation, { replace: true });
      return;
    }
    actionInFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await billingRequest('decline', offer.id);
      if (result.status === 'completed') {
        setConfirmed(true);
        return;
      }
      if (result.status === 'pending')
        throw new Error('Please check the pending payment before continuing.');
      track('annual_offer_declined', offer);
      navigate(continuation, { replace: true });
    } catch (declineError) {
      setError(declineError instanceof Error ? declineError.message : 'Please retry.');
    } finally {
      setBusy(false);
      actionInFlight.current = false;
    }
  };
  const expired = loaded && offer?.status === 'open' && remaining === 0;
  const clock = `${Math.floor(remaining / 60)
    .toString()
    .padStart(2, '0')}:${(remaining % 60).toString().padStart(2, '0')}`;

  return (
    <main className="annual-offer-page">
      <div className="annual-offer-brand">
        BotSpot<span>TRADE</span>
      </div>
      {!loaded ? (
        <div role="status" className="annual-offer-loading">
          Your payment is complete. Preparing your annual offer…
        </div>
      ) : confirmed ? (
        <section className="annual-offer-success" aria-labelledby="annual-success-heading">
          <Check size={36} aria-hidden="true" />
          <h1 id="annual-success-heading">You’re set for the year.</h1>
          <p>Your {offer?.productName} plan is now annual. Your monthly payment was credited.</p>
          <button
            className="annual-offer-primary"
            onClick={() => navigate(continuation, { replace: true })}
          >
            Continue to BotSpot
          </button>
        </section>
      ) : (
        <>
          <header className="annual-offer-intro">
            <p className="annual-offer-paid">
              <Check size={16} aria-hidden="true" /> Your monthly plan is active
            </p>
            <h1>
              {offer ? `Get ${offer.monthsFree} months on us.` : 'Your purchase is complete.'}
            </h1>
            <p>
              {offer
                ? `Switch ${offer.productName} to annual. One payment, a full year of access.`
                : 'Continue to your account, or sign in to review the annual offer.'}
            </p>
          </header>
          {offer && (
            <section className="annual-offer-card" aria-label="Annual plan offer">
              <div className="annual-offer-card-heading">
                <h2>Your annual offer</h2>
                <span className="annual-offer-clock" aria-label={`Offer time remaining ${clock}`}>
                  <Clock3 size={15} aria-hidden="true" />
                  {clock}
                </span>
              </div>
              <ul className="annual-offer-benefits">
                <li>
                  <Check size={18} aria-hidden="true" />
                  12 months for the price of {12 - offer.monthsFree}
                  {offer.monthsFree > 2 ? ' in your first year' : ''}
                </li>
                <li>
                  <Check size={18} aria-hidden="true" />
                  Your monthly payment is credited below
                </li>
                <li>
                  <Check size={18} aria-hidden="true" />
                  Keep the same plan and features
                </li>
              </ul>
              <div className="annual-offer-billing-header">
                <strong>{offer.productName} · Annual</strong>
                <span>
                  {offer.quote.cardBrand} •••• {offer.quote.cardLast4}
                </span>
              </div>
              <dl className="annual-offer-bill">
                <div>
                  <dt>{offer.monthsFree > 2 ? 'First-year annual price' : 'Annual price'}</dt>
                  <dd>{money(offer.quote.annualAmount)}</dd>
                </div>
                <div>
                  <dt>Unused monthly payment credit</dt>
                  <dd>−{money(offer.quote.monthlyCredit)}</dd>
                </div>
                {offer.quote.accountCredit > 0 && (
                  <div>
                    <dt>Account credit</dt>
                    <dd>−{money(offer.quote.accountCredit)}</dd>
                  </div>
                )}
                <div>
                  <dt>Tax</dt>
                  <dd>{money(offer.quote.taxAmount)}</dd>
                </div>
                <div className="annual-offer-due">
                  <dt>Due today</dt>
                  <dd>
                    {money(offer.quote.amountDue)}{' '}
                    <small>{offer.quote.currency.toUpperCase()}</small>
                  </dd>
                </div>
              </dl>
              <p className="annual-offer-renewal">
                Auto-renews on{' '}
                {new Date(offer.quote.renewalDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}{' '}
                at {money(offer.quote.renewalAmount)}/year, plus applicable tax.{' '}
                {offer.monthsFree > 2 ? 'This offer applies to your first year. ' : ''}Cancel
                renewal anytime in Billing.
              </p>
              {expired && (
                <p role="status" className="annual-offer-expired">
                  This welcome offer has expired. Your monthly plan is active.
                </p>
              )}
              {error && (
                <p role="alert" className="annual-offer-error">
                  {error}
                </p>
              )}
              <button className="annual-offer-primary" onClick={claim} disabled={busy || expired}>
                {busy
                  ? 'Checking your payment…'
                  : offer.status === 'pending'
                    ? 'Check annual payment'
                    : `Pay ${money(offer.quote.amountDue)} & switch to annual`}
              </button>
              <button className="annual-offer-secondary" onClick={keepMonthly} disabled={busy}>
                Keep my monthly plan
              </button>
              <p className="annual-offer-secure">
                <LockKeyhole size={14} aria-hidden="true" />
                Secure payment with Stripe
              </p>
            </section>
          )}
          {!offer && (
            <div className="annual-offer-loading">
              {error && <p role="alert">{error}</p>}
              <button className="annual-offer-primary" onClick={keepMonthly}>
                Continue to BotSpot
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
